/**
 * A number series field (Phase 8 of docs/plans/workbook-map.md, W17 to W23):
 * its spec, read back without trusting it, its shape rules, and how its number
 * is drawn.
 *
 * The number is the value. It is kept in a number slot, so the list sorts and
 * filters by it, a formula reads it and search finds "41". The prefix and the
 * year part are drawn from the spec when it is shown (W23).
 *
 * The database gives the number (`app.take_field_series_number`, 0096); this
 * file only says what the shape is and how a number looks.
 */
import {
  FIELD_SERIES_RULES,
  SERIES_RESETS,
  SERIES_YEAR_STYLES,
  formatDocNumber,
  seriesPeriodOf,
  seriesShapeProblems,
  type DocSeriesProblem,
  type DocSeriesReset,
  type DocSeriesYearStyle,
  type SeriesShape,
} from './doc-numbering';

/**
 * The spec of a `series` field. The shape copies the document series (W18);
 * `per` is the field each value of which has its own counter (W19), a choice
 * or a pick of the same record, or null for one series; `once` is "Each
 * number once" (W20), on unless it is turned off.
 *
 * In a setup spec `per` is a field key, and apply rewrites it to the id.
 */
export type SeriesSpec = {
  readonly prefix: string;
  readonly yearStyle: DocSeriesYearStyle;
  readonly digits: number;
  readonly startNumber: number;
  readonly reset: DocSeriesReset;
  readonly fyStartMonth: number;
  readonly per: string | null;
  readonly once: boolean;
};

/** The shape a new series field starts with: plain numbers from 1, never restarting, each once. */
export const SERIES_DEFAULT: SeriesSpec = {
  prefix: '',
  yearStyle: 'none',
  digits: 1,
  startNumber: 1,
  reset: 'never',
  fyStartMonth: 4,
  per: null,
  once: true,
};

/**
 * A stored or sent `series` spec, read without trusting it. A key that is
 * absent takes its default; a key that is there and does not read makes the
 * whole spec null, so a shape nobody sent is never stored. The shape rules
 * (`fieldSeriesProblems`) are judged after.
 */
export const readSeriesSpec = (raw: unknown): SeriesSpec | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as Record<string, unknown>;
  // What did not read is BAD, never null: `per` may be null, one count, and that reads.
  const BAD = Symbol('bad');
  const pick = <T>(key: keyof SeriesSpec, ok: (value: unknown) => value is T): T | undefined | typeof BAD => {
    const value = held[key];
    if (value === undefined) return undefined;
    return ok(value) ? value : BAD;
  };
  const isString = (value: unknown): value is string => typeof value === 'string';
  const isWhole = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value);
  const isYear = (value: unknown): value is DocSeriesYearStyle => (SERIES_YEAR_STYLES as readonly unknown[]).includes(value);
  const isReset = (value: unknown): value is DocSeriesReset => (SERIES_RESETS as readonly unknown[]).includes(value);
  const isPer = (value: unknown): value is string | null => value === null || (typeof value === 'string' && value !== '');
  const isBool = (value: unknown): value is boolean => typeof value === 'boolean';
  const prefix = pick('prefix', isString);
  const yearStyle = pick('yearStyle', isYear);
  const digits = pick('digits', isWhole);
  const startNumber = pick('startNumber', isWhole);
  const reset = pick('reset', isReset);
  const fyStartMonth = pick('fyStartMonth', isWhole);
  const per = pick('per', isPer);
  const once = pick('once', isBool);
  if (prefix === BAD || yearStyle === BAD || digits === BAD || startNumber === BAD || reset === BAD || fyStartMonth === BAD || per === BAD || once === BAD) return null;
  return {
    prefix: prefix ?? SERIES_DEFAULT.prefix,
    yearStyle: yearStyle ?? SERIES_DEFAULT.yearStyle,
    digits: digits ?? SERIES_DEFAULT.digits,
    startNumber: startNumber ?? SERIES_DEFAULT.startNumber,
    reset: reset ?? SERIES_DEFAULT.reset,
    fyStartMonth: fyStartMonth ?? SERIES_DEFAULT.fyStartMonth,
    per: per ?? null,
    once: once ?? true,
  };
};

/** Every problem of a series field's shape, at once: the document series' rules, with an empty prefix and no padding allowed. */
export const fieldSeriesProblems = (spec: SeriesShape): readonly DocSeriesProblem[] => seriesShapeProblems(spec, FIELD_SERIES_RULES);

/**
 * Whether a change keeps the frozen shape (W18, SERIES_IN_USE): once the field
 * has given a number, its prefix, year part, digits, start, reset, year start
 * and "counted separately for each" stay. "Each number once" may change.
 */
export const seriesShapeKept = (was: SeriesSpec, now: SeriesSpec): boolean =>
  was.prefix === now.prefix &&
  was.yearStyle === now.yearStyle &&
  was.digits === now.digits &&
  was.startNumber === now.startNumber &&
  was.reset === now.reset &&
  was.fyStartMonth === now.fyStartMonth &&
  was.per === now.per;

/** The counter period an entry's day falls in: its financial year's start year, or `all`. */
export const seriesPeriodFor = (spec: SeriesSpec, day: string): string => seriesPeriodOf(spec, day);

/**
 * The number as it is shown (W23): prefix, year part and digits. `day` is the
 * entry's day (`YYYY-MM-DD`), which the year part is cut from; with no day, or
 * a series with no year part, it is the prefix and the digits alone.
 */
export const formatSeriesNumber = (spec: SeriesSpec, n: number, day: string | null): string => {
  if (day !== null && /^\d{4}-\d{2}-\d{2}/.test(day)) return formatDocNumber(spec, day.slice(0, 10), n);
  const counter = String(n);
  return `${spec.prefix}${counter.length >= spec.digits ? counter : counter.padStart(spec.digits, '0')}`;
};

/**
 * A series field's value in words, as a list or an entry shows it (W23): its
 * number drawn through the field's spec, the entry's day giving the year part.
 * Null when there is no number.
 */
export const seriesWordOf = (spec: SeriesSpec | null, value: unknown, day: unknown): string | null => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;
  if (!Number.isFinite(n)) return null;
  return formatSeriesNumber(spec ?? SERIES_DEFAULT, Math.trunc(n), typeof day === 'string' ? day : null);
};
